import { API } from "../api";
const BASE_URL = "/cooperatives/calendar_events";

const eventsExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query({
      query: ({ page = 1, event_type, search, attendees, is_active } = {}) => {
        let url = `${BASE_URL}?guard=staff&page=${page}`;

        // Add event type filter
        if (event_type) {
          url += `&event_type=${event_type}`;
        }

        // Add search filter
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }

        // Add attendees filter
        if (attendees) {
          url += `&attendees=${attendees}`;
        }

        // Add active status filter
        if (is_active !== undefined) {
          url += `&is_active=${is_active}`;
        }

        return url;
      },
      transformResponse: (responseData) => {
        const { event_calendars = [], pagination = {} } = responseData.data;
        return {
          event_calendars,
          pagination,
        };
      },
      providesTags: (result, error, arg) => {
        if (!result?.event_calendars)
          return [{ type: "AdminEvent", id: "LIST" }];
        return [
          { type: "AdminEvent", id: "LIST" },
          result?.event_calendars?.map(({ id }) => ({
            type: "AdminEvent",
            id: id,
          })),
        ];
      },
    }),
    // New endpoint for past events
    getPastEvents: builder.query({
      query: ({ page = 1, search, attendees, is_active } = {}) => {
        let url = `${BASE_URL}/past?guard=staff&page=${page}`;

        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        if (attendees) {
          url += `&attendees=${attendees}`;
        }
        if (is_active !== undefined) {
          url += `&is_active=${is_active}`;
        }

        return url;
      },
      transformResponse: (responseData) => {
        const { event_calendars = [], pagination = {} } = responseData.data;
        return {
          event_calendars,
          pagination,
        };
      },
      providesTags: [{ type: "AdminEvent", id: "PAST" }],
    }),
    // New endpoint for current events
    getCurrentEvents: builder.query({
      query: ({ page = 1, search, attendees, is_active } = {}) => {
        let url = `${BASE_URL}/current?guard=staff&page=${page}`;

        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        if (attendees) {
          url += `&attendees=${attendees}`;
        }
        if (is_active !== undefined) {
          url += `&is_active=${is_active}`;
        }

        return url;
      },
      transformResponse: (responseData) => {
        const { event_calendars = [], pagination = {} } = responseData.data;
        return {
          event_calendars,
          pagination,
        };
      },
      providesTags: [{ type: "AdminEvent", id: "CURRENT" }],
    }),
    // New endpoint for upcoming events
    getUpcomingEvents: builder.query({
      query: ({ page = 1, search, attendees, is_active } = {}) => {
        let url = `${BASE_URL}/upcoming?guard=staff&page=${page}`;

        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        if (attendees) {
          url += `&attendees=${attendees}`;
        }
        if (is_active !== undefined) {
          url += `&is_active=${is_active}`;
        }

        return url;
      },
      transformResponse: (responseData) => {
        const { event_calendars = [], pagination = {} } = responseData.data;
        return {
          event_calendars,
          pagination,
        };
      },
      providesTags: [{ type: "AdminEvent", id: "UPCOMING" }],
    }),
    getSingleEvent: builder.query({
      query: (id) => `${BASE_URL}/${id}?guard=staff`,
      providesTags: (result, err, arg) => [{ type: "AdminEvent", id: arg }],
      transformResponse: (responseData) => responseData.data,
    }),
    getSingleEventWithHistory: builder.query({
      query: (id) => `${BASE_URL}/${id}?guard=staff&include_history=true`,
      providesTags: (result, err, arg) => [{ type: "AdminEvent", id: `${arg}-history` }],
      transformResponse: (responseData) => responseData.data,
    }),
    addEvent: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/add?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "AdminEvent", id: "LIST" }],
    }),
    editEvent: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/${body.id}/edit?guard=staff`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AdminEvent", id: arg },
      ],
    }),
    deleteEvent: builder.mutation({
      query: (id) => ({
        url: `${BASE_URL}/${id}/delete?guard=staff`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AdminEvent", id: arg },
      ],
    }),
    resendCommunications: builder.mutation({
      query: (id) => ({
        url: `${BASE_URL}/${id}/resend-communications?guard=staff`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "AdminEvent", id: arg },
        { type: "AdminEvent", id: `${arg}-history` },
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetPastEventsQuery,
  useGetCurrentEventsQuery,
  useGetUpcomingEventsQuery,
  useAddEventMutation,
  useGetSingleEventQuery,
  useGetSingleEventWithHistoryQuery,
  useEditEventMutation,
  useDeleteEventMutation,
  useResendCommunicationsMutation,
} = eventsExtendedApi;
